import { FC, useEffect, useState } from 'react';

import { vacancyStatisticsHHLink } from 'utils/clickme';
import { VIEW_DATE_WITH_MONTH_FORMAT } from 'utils/constants/date';
import { formatDate } from 'utils/date';

import dashboardCampaignButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/dashboard_campaign_button_click';
import { VSpacingContainer } from '@hh.ru/magritte-ui';

import BlokoLink from 'bloko/blocks/link';
import Text from 'bloko/blocks/text';
import VSpacing from 'bloko/blocks/vSpacing';

import useTrls from 'hooks/translation/useTrls';

import CampaignAdminActions from 'components/CampaignAdminActions';
import StatisticLink from 'components/StatisticLink';

import Actions from 'components/CampaignCard/CampaignInfo/Actions';
import CampaignBalanceLink from 'components/CampaignCard/CampaignInfo/CampaignBalanceLink/CampaignBalanceLink';
import Targetings from 'components/CampaignCard/CampaignInfo/Targetings';
import Regions from 'components/CampaignCard/CampaignInfo/Targetings/Regions/Regions';
import VacancyId from 'components/CampaignCard/CampaignInfo/Targetings/VacancyId/VacancyId';
import DashboardAveragePricePerClick from 'components/Dashboard/DashboardAveragePricePerClick/DashboardAveragePricePerClick';

import { Campaign, CampaignTabStatus } from 'store/models/dashboard';
import { useSelector } from 'store/utils';

import styles from 'components/CampaignCard/CampaignInfo/CampaignInfo.module.less';

interface CampaignInfoProps {
    campaign: Campaign;
    isAuction: boolean;
    realBalance: number;
    userBalance: number;
}

const CampaignInfo: FC<CampaignInfoProps> = ({ campaign, userBalance, realBalance, isAuction }) => {
    const currentTab = useSelector((state) => state.campaignTabsStatus);
    const [isShowBalanceLink, setShowBalanceLink] = useState(false);
    const {
        age,
        allBanners,
        areas,
        archiveReason,
        budget,
        createdAt,
        deviceType,
        frontendStatus,
        id: campaignId,
        professionalRoles,
        sex,
        sources,
        statistics,
        vacancy,
        vacancyId,
        startAt,
        endAt,
    } = campaign;

    const { averagePricePerClickInCents } = statistics;

    const trl = useTrls();

    const sendAnalytics = () => {
        dashboardCampaignButtonClick({ buttonName: 'vacancy_statistics', campaignId });
    };

    const currentBalance = budget ? Math.round((budget - statistics.amount) * 100) / 100 : 0;
    const urlToStatistics = `/new/campaign/${campaignId}/statistics/`;

    useEffect(() => {
        if (budget && currentTab !== CampaignTabStatus.Off) {
            setShowBalanceLink(true);
        }
    }, [budget, currentTab]);

    return (
        <>
            <div className={styles.campaignInfo}>
                <div className={styles.campaignInfoTargetings} data-qa={`campaign_info_targetings_${campaignId}`}>
                    {isAuction && vacancy ? (
                        <VSpacingContainer default={4}>
                            <BlokoLink href={`https://hh.ru/vacancy/${vacancy.vacancyId}`}>{vacancy.name}</BlokoLink>
                            <VacancyId vacancyId={vacancy.vacancyId} />
                            <Regions campaignAreas={areas} />
                        </VSpacingContainer>
                    ) : (
                        <Targetings
                            age={age}
                            areas={areas}
                            deviceType={deviceType}
                            professionalRoles={professionalRoles}
                            sources={sources}
                            sex={sex}
                            vacancy={vacancy}
                        />
                    )}
                </div>
                <div className={styles.campaignInfoStatistics}>
                    <VSpacingContainer default={16}>
                        <div>
                            <Text>{trl('campaign.statistic.title')}</Text>
                            <VSpacing base={1} />
                            <StatisticLink
                                {...statistics}
                                action={urlToStatistics}
                                dataQa={`campaign_statistics_${campaignId}`}
                                campaignId={campaignId}
                            />
                        </div>
                        {!!averagePricePerClickInCents && (
                            <DashboardAveragePricePerClick
                                averagePricePerClickInCents={averagePricePerClickInCents}
                                urlToStatistics={urlToStatistics}
                                dataQa={`campaign_average_price_per_click_${campaignId}`}
                            />
                        )}
                        {isShowBalanceLink && campaignId && (
                            <CampaignBalanceLink
                                campaignId={campaignId}
                                balance={currentBalance > 0 ? currentBalance : 0}
                                budget={budget || 0}
                                isAuction={isAuction}
                                vacancyId={vacancyId}
                            />
                        )}
                        {vacancyId && (
                            <BlokoLink
                                data-qa="stat-link-hh"
                                href={vacancyStatisticsHHLink(vacancyId)}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={sendAnalytics}
                            >
                                {trl('campaign.statistic.link.hh')}
                            </BlokoLink>
                        )}
                    </VSpacingContainer>
                </div>
                <div className={styles.campaignInfoCreated}>
                    <Text>{trl('campaign.createdAt.title')}</Text>
                    <VSpacing base={1} />
                    {formatDate(createdAt, VIEW_DATE_WITH_MONTH_FORMAT)}
                </div>
                <div className={styles.campaignInfoActions}>
                    <Actions
                        campaignId={campaignId}
                        frontendStatus={frontendStatus}
                        archiveReason={archiveReason}
                        userBalance={userBalance}
                        realBalance={realBalance}
                        banners={allBanners}
                        isAuction={isAuction}
                        vacancyId={vacancyId}
                        startAt={startAt}
                        endAt={endAt}
                    />
                    <CampaignAdminActions campaignId={campaignId} frontendStatus={frontendStatus} />
                </div>
            </div>
            <VSpacing base={10} />
        </>
    );
};

export default CampaignInfo;
